import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Camera, Check, FileCheck2, X } from "lucide-react";
import { api, errMsg, photoUrl } from "@/lib/api";
import { fmtDate } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";

const TONE = { ok: "border-l-emerald-500", soon: "border-l-amber-500", expired: "border-l-red-600", missing: "border-l-red-600" };
export const stateText = (i) => (i.state === "missing" ? "No date on file" : i.state === "expired" ? `Expired ${fmtDate(i.expires)}` : i.state === "soon" ? `Runs out ${fmtDate(i.expires)} \u00b7 ${i.days} days` : `Valid to ${fmtDate(i.expires)}`);

const Pending = ({ s, onDone }) => {
  const act = async (a) => {
    const note = a === "reject" ? window.prompt("Reason (the rider will see this)") : "";
    if (note === null) return;
    try { await api.post(`/rider-docs/submissions/${s.id}/${a}`, { note }); toast.success(a === "approve" ? "Approved" : "Sent back to rider"); onDone(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="mt-2 flex flex-wrap items-center gap-2 rounded-lg bg-amber-500/10 p-2 text-xs" data-testid={`doc-pending-${s.kind}`}>
      <a href={photoUrl(s.proof)} target="_blank" rel="noreferrer"><img src={photoUrl(s.proof)} alt="proof" className="h-14 w-14 rounded object-cover" /></a>
      <span className="flex-1">Rider sent new expiry <b>{fmtDate(s.expires)}</b></span>
      <button onClick={() => act("approve")} data-testid={`doc-approve-${s.kind}`} className="inline-flex items-center gap-1 rounded-full bg-emerald-600 px-3 py-1.5 font-bold text-white"><Check className="h-3 w-3" />Approve</button>
      <button onClick={() => act("reject")} data-testid={`doc-reject-${s.kind}`} className="inline-flex items-center gap-1 rounded-full border px-3 py-1.5 font-bold"><X className="h-3 w-3" />Reject</button>
    </div>
  );
};

const ManageRow = ({ i, riderId, reload }) => {
  const [d, setD] = useState(i.expires || "");
  const save = async () => { try { await api.put(`/rider-docs/${riderId}/${i.kind}`, { expires: d }); toast.success(`${i.label} saved`); reload(); } catch (e) { toast.error(errMsg(e)); } };
  if (i.kind === "assessment") return null;
  return (
    <div className="mt-2 flex flex-wrap items-center gap-2">
      <input type="date" value={d} onChange={(e) => setD(e.target.value)} data-testid={`doc-date-${i.kind}`} className="h-9 rounded-lg border bg-background px-2 text-sm" />
      <button onClick={save} disabled={!d || d === i.expires} data-testid={`doc-save-${i.kind}`} className="rounded-full bg-primary px-4 py-1.5 text-xs font-bold text-primary-foreground disabled:opacity-40">Save</button>
      {i.proof && <a href={photoUrl(i.proof)} target="_blank" rel="noreferrer" className="text-xs underline">View proof</a>}
    </div>
  );
};

const SubmitRow = ({ i, reload }) => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ expires: "", proof: null, busy: false });
  const upload = async (file) => {
    if (!file) return;
    setF((x) => ({ ...x, busy: true }));
    try { const fd = new FormData(); fd.append("file", file); const { data } = await api.post("/photos", fd); setF((x) => ({ ...x, proof: data.path, busy: false })); }
    catch (e) { toast.error(errMsg(e)); setF((x) => ({ ...x, busy: false })); }
  };
  const send = async () => { try { await api.post(`/rider-docs/mine/${i.kind}`, { expires: f.expires, proof: f.proof }); toast.success("Sent for approval"); setOpen(false); reload(); } catch (e) { toast.error(errMsg(e)); } };
  if (i.kind === "assessment") return null;
  if (i.pending) return <p className="mt-2 text-sm font-semibold text-amber-500" data-testid={`doc-waiting-${i.kind}`}>New date {fmtDate(i.pending.expires)} sent - waiting for admin approval</p>;
  const rejected = i.rejected && <p className="mt-2 text-sm font-semibold text-red-500" data-testid={`doc-rejected-${i.kind}`}>Your last photo wasn't accepted{i.rejected.note ? `: ${i.rejected.note}` : ""} - please send it again.</p>;
  if (!open) return <>{rejected}<button onClick={() => setOpen(true)} data-testid={`doc-update-${i.kind}`} className="mt-2 min-h-12 rounded-2xl border-2 px-5 text-sm font-bold">Send new document</button></>;
  return (
    <div className="mt-3 space-y-3">
      <label className="block text-sm font-semibold">New expiry date<input type="date" value={f.expires} onChange={(e) => setF({ ...f, expires: e.target.value })} data-testid={`doc-new-date-${i.kind}`} className="mt-1 h-12 w-full rounded-xl border bg-background px-3" /></label>
      <label className="flex min-h-12 cursor-pointer items-center justify-center gap-2 rounded-2xl border-2 border-dashed text-sm font-bold" data-testid={`doc-photo-${i.kind}`}>
        <Camera className="h-5 w-5" />{f.busy ? "Uploading..." : f.proof ? "Photo added - tap to change" : "Take or choose a photo"}
        <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
      </label>
      {f.proof && <img src={photoUrl(f.proof)} alt="proof" className="h-24 rounded-lg object-cover" />}
      <div className="flex gap-2"><button onClick={send} disabled={!f.expires || !f.proof} data-testid={`doc-send-${i.kind}`} className="min-h-12 flex-1 rounded-2xl bg-primary font-bold text-primary-foreground disabled:opacity-40">Send for approval</button>
        <button onClick={() => setOpen(false)} data-testid={`doc-cancel-${i.kind}`} className="min-h-12 rounded-2xl border px-4">Cancel</button></div>
    </div>
  );
};

export const RiderDocs = ({ riderId, manage, mine }) => {
  const [items, setItems] = useState(null);
  const load = useCallback(() => api.get(mine ? "/rider-docs/mine" : `/rider-docs/${riderId}`).then((r) => setItems(r.data.items)).catch(() => setItems([])), [mine, riderId]);
  useEffect(() => { load(); }, [load]);
  if (!items) return null;
  return (
    <section className="space-y-3 rounded-2xl border bg-card p-5" data-testid="rider-docs">
      <h2 className="flex items-center gap-2 font-bold"><FileCheck2 className="h-4 w-4" /> Renewals & documents</h2>
      <p className="text-xs text-muted-foreground">Riders can't be given jobs or rider shifts if any of these is missing or expired.</p>
      {items.map((i) => (
        <div key={i.kind} className={`rounded-xl border border-l-4 p-3 ${TONE[i.state]}`} data-testid={`doc-${i.kind}`}>
          <div className="flex flex-wrap items-center justify-between gap-2"><span className="font-semibold">{i.label}</span>
            <span className={`text-sm font-bold ${i.state === "ok" ? "text-emerald-500" : i.state === "soon" ? "text-amber-500" : "text-red-500"}`} data-testid={`doc-state-${i.kind}`}>{stateText(i)}</span></div>
          {manage && <ManageRow i={i} riderId={riderId} reload={load} />}
          {manage && i.pending && <Pending s={i.pending} onDone={load} />}
          {mine && <SubmitRow i={i} reload={load} />}
        </div>
      ))}
    </section>
  );
};

export const MyExpiryBanner = () => {
  const [bad, setBad] = useState([]);
  useEffect(() => { api.get("/rider-docs/mine").then((r) => setBad(r.data.items.filter((i) => i.state !== "ok"))).catch(() => {}); }, []);
  if (!bad.length) return null;
  const red = bad.some((i) => i.state !== "soon");
  return (
    <Link to="/my" data-testid="my-expiry-banner" className={`block rounded-2xl border-2 p-4 ${red ? "border-red-600 bg-red-600/10" : "border-amber-500 bg-amber-500/10"}`}>
      <p className={`font-bold ${red ? "text-red-500" : "text-amber-500"}`}>{red ? "Action needed before you can ride" : "Renewal due soon"}</p>
      <ul className="mt-1 text-sm">{bad.map((i) => <li key={i.kind}>{i.label}: {stateText(i)}</li>)}</ul>
      <p className="mt-1 text-xs text-muted-foreground">Tap to send a photo of the new document</p>
    </Link>
  );
};

export const ControlExpiryBanner = () => {
  const { user } = useAuth();
  const canOpen = (user.permissions?.riders || 0) >= 1;
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(() => { api.get("/rider-docs/expiring").then((r) => setRows(r.data)).catch(() => {}); }, []);
  if (!rows.length) return null;
  const blocked = rows.filter((x) => x.items.some((i) => i.state !== "soon")).length;
  return (
    <section className="rounded-2xl border border-amber-500 bg-amber-500/10 p-4" data-testid="control-expiry-banner">
      <button onClick={() => setOpen(!open)} className="flex w-full flex-wrap items-center justify-between gap-2 text-left" data-testid="control-expiry-toggle">
        <span className="font-bold text-amber-500">Renewals: {[blocked && `${blocked} rider${blocked === 1 ? "" : "s"} can't ride`, rows.length - blocked && `${rows.length - blocked} due in 30 days`].filter(Boolean).join(" \u00b7 ")}</span>
        <span className="text-xs font-semibold underline">{open ? "Hide" : "Show"}</span>
      </button>
      {open && <ul className="mt-2 space-y-1 text-sm">{rows.map((x) => (
        <li key={x.rider_id} data-testid={`expiry-row-${x.rider_id}`}>{canOpen ? <Link to={`/riders/${x.rider_id}`} className="font-bold underline-offset-2 hover:underline">{x.name}</Link> : <b>{x.name}</b>}: {x.items.map((i) => `${i.label} - ${stateText(i)}`).join(" \u00b7 ")}</li>
      ))}</ul>}
    </section>
  );
};

export const DocApprovals = () => {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/rider-docs/submissions").then((r) => setRows(r.data)).catch(() => {}); }, []);
  if (!rows.length) return null;
  return (
    <section className="rounded-2xl border border-amber-500 bg-amber-500/10 p-4" data-testid="doc-approvals">
      <p className="font-bold text-amber-500">{rows.length} document{rows.length > 1 ? "s" : ""} waiting for approval</p>
      <ul className="mt-1 space-y-1 text-sm">{rows.map((s) => <li key={s.id}><Link to={`/riders/${s.rider_id}`} className="font-semibold underline" data-testid={`doc-approval-${s.id}`}>{s.rider_name}</Link>{" \u00b7 "}{s.label}{" \u00b7 "}new expiry {fmtDate(s.expires)}</li>)}</ul>
    </section>
  );
};
